import { BankAccountSchema, MoneySchema, WITHDRAWAL_STATUSES } from '@pf/contracts';
import { z } from 'zod';
/**
 * A withdrawal request as the *website* presents it to its own customer.
 *
 * PaymentFlow has a withdrawal record too (`GET /v1/withdrawals`), and it is deliberately not
 * this one. PF's view is a reconciliation feed for a server; this is a person's view of their own
 * money: what it is worth, where it will land, whether anyone has claimed it yet, and the lock
 * reference they can quote to support. The BFF is what joins the two.
 */
/**
 * States a withdrawal can be in from the customer's point of view.
 *
 * The seven from SAD §4B are PaymentFlow's, and pass through unchanged. The three prefixed states
 * are the *website's* — they cover the window in which funds are locked on the website's ledger
 * but PaymentFlow has not (yet, or ever) accepted the request. That window is real and a customer
 * can observe it, so the profile names it rather than pretending a request is `AVAILABLE` before
 * PF has said so.
 */
export const CUSTOMER_WITHDRAWAL_STATUSES = [
    ...WITHDRAWAL_STATUSES,
    /** Funds locked, request not yet accepted by PaymentFlow. Normally milliseconds. */
    'PENDING_SUBMIT',
    /**
     * PaymentFlow refused the request or was unreachable. The lock is deliberately *not* released
     * on this path — PF may have processed the request and lost the response — so reconciliation
     * resolves it (SAD §10). The customer sees a request that is stuck, not one that vanished.
     */
    'SUBMIT_FAILED',
    /** Reconciliation decided the lock was an orphan and released it. Terminal. */
    'ORPHAN_RELEASED',
];
/** Customer-facing withdrawal status. */
export const CustomerWithdrawalStatusSchema = z.enum(CUSTOMER_WITHDRAWAL_STATUSES);
/** One withdrawal request, as the customer area renders it. */
export const CustomerWithdrawalSchema = z
    .object({
    /**
     * The website's own handle for this request, and the value the UI puts in
     * `POST /bff/withdrawals/{id}/cancel`. Not PaymentFlow's withdrawal id — see
     * {@link CustomerWithdrawalSchema.withdrawal_id}.
     */
    id: z.string().min(1),
    status: CustomerWithdrawalStatusSchema,
    amount: MoneySchema,
    /**
     * The funds-lock receipt (SAD §3 D2). The website locks the money and mints this *before*
     * telling PaymentFlow, so it exists even for a request PF never accepted — which is exactly
     * what makes it the right thing to show a worried customer.
     */
    lock_reference: z.string().min(1),
    /** PaymentFlow's id for the request, once accepted. `null` while `PENDING_SUBMIT`/`SUBMIT_FAILED`. */
    withdrawal_id: z.string().min(1).nullable(),
    /** Where the money will be sent. */
    receiver_account: BankAccountSchema,
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    /**
     * Position in the order book at this amount, oldest first. `null` once the request is no
     * longer `AVAILABLE`, and `null` for any BFF that cannot see the book — PaymentFlow's
     * `/v1/deposit-options` reports availability per amount, not per request, so a BFF only knows
     * this if it tracks it. The reference UI hides the queue line rather than showing "unknown".
     */
    pool_position: z.number().int().positive().nullable(),
    /** How many requests sit at this amount in total. `null` under the same conditions. */
    pool_depth: z.number().int().positive().nullable(),
    /** Set once a depositor is matched, so the customer can see progress. */
    matched_transaction_id: z.string().min(1).nullable(),
})
    .strict();
/** `POST /bff/withdrawals` — the website's own "request a withdrawal" form. */
export const CreateCustomerWithdrawalRequestSchema = z
    .object({
    amount: MoneySchema,
    receiver_account: BankAccountSchema,
})
    .strict();
/** `GET /bff/withdrawals` — the customer's own requests, newest first. */
export const CustomerWithdrawalListSchema = z
    .object({ items: z.array(CustomerWithdrawalSchema) })
    .strict();
//# sourceMappingURL=withdrawal.js.map